import Component from 'core/Component';
import { mix } from 'core/mixwith';
import { deepMerge } from 'toolbox/deepMerge';
import { on, off } from 'toolbox/event';
import { cookie } from 'toolbox/cookie';
import Accessibility from 'mixins/Accessibility';
import { Event } from 'services/EventEmitter';
import DataLayer from 'services/DataLayer';

/**
 * This is a description of the Toaster constructor function.
 * @class
 * @classdesc This is a description of the Toaster class. (must be edited)
 * @extends Component
 */
export default class Toaster extends mix(Component).with(Accessibility) {
    /**
     * Constructor of the class that mainly merge the options of the components
     * @param {HTMLElement} element HTMLElement of the component
     * @param {Object} options options that belongs to the component
     */
    constructor(element, options = {}) {
        super(element, deepMerge({
            // auto expand after {autoDisplay} ms
            autoDisplay: 0,
            // set to `0` to disable it
            // scroll treshold to display the toaster and requires autoDisplay to equal 0
            autoDisplayTreshold: 300,
            // expand only on click (from collapsed state), ignore autoDisplay and autoDisplayTreshold
            keepCollapsed: true,
            // days, to set cookie lifetime
            cookieDuration: 365,
            // show overlay and add event listener on overlay close
            showOverlay: false,
            _scrollable: true,
            ariaLabel: '', // If defined, this copy will be used as the aria-label of a Modal to pronounce the modal title by screen reader. If empty, the text with the id="toaster-title" will be pronounced
        }, options));
    }

    /**
     * All selectors must be cached. Never cache elements that are out of the component scope
     */
    initCache() {
        this.selectors.declineBtn = this.element.querySelector('[data-js-toaster-decline]');
        this.selectors.closeBtn = this.element.querySelector('[data-js-toaster-close]');
        this.selectors.closeDialogBtn = this.element.querySelector('[data-js-toaster-dialog-close]');
        this.selectors.heading = this.element.querySelector('[data-js-toaster-heading]');
        this.selectors.container = this.element.querySelector('[data-js-toaster-container]');
        this.selectors.titleBtn = this.element.querySelector('[data-js-toaster-button]');
    }

    /**
     * Init the different state of the component
     * It helps to avoid heavy DOM manipulation
     */
    initState() {
        this.state.isOpen = false;
        this.state.isFirstOpen = false;
        this.state.lastScrollTop = 0;
    }

    /**
     * Should contain only event listeners and nothing else
     * All the event handlers should be into a separated function. No usage of anonyous function
     */
    bindEvents() {
        on('click', this.selectors.closeBtn, this.onClose.bind(this));
        on('click', this.selectors.closeDialogBtn, this.onClose.bind(this));
        if (this.selectors.declineBtn) {
            on('click', this.selectors.declineBtn, this.onDecline.bind(this));
        }
        on('click', this.selectors.heading, this.onTriggerToggleDisplay.bind(this));
        on('keydown', this.selectors.container, this.onKeyboardControl.bind(this));
        Event.on('toaster.close', this.onToasterClose, this);
        if (this.options.showOverlay) {
            Event.on('overlay.close.before', this.onOverlayCloseBefore, this);
        }
        Event.on('modal.open', this.onModalOpen, this);
    }

    /**
     * After init
     * Run any script after the component is fully initialized
     */
    afterInit() {
        if (this.isHidden()) {
            this.remove();
        }

        Event.emit('registry.registerChildren', this.element);
        this.state.lastScrollTop = window.scrollY || document.documentElement.scrollTop;
        const { autoDisplay } = this.options;
        if (autoDisplay && typeof autoDisplay === 'number' && !this.options.keepCollapsed && !this.state.isFirstOpen) {
            setTimeout(() => this.toggleDisplay(true), autoDisplay);
        }
    }

    /**
     * Handlers to onscroll event
     */
    onScroll() {
        const isAutoDisplay = this.options.autoDisplay === 0 && this.options.autoDisplayTreshold > 0;
        if (isAutoDisplay && !this.state.isFirstOpen && !this.options.keepCollapsed) {
            this.handleDisplay();
        }
    }

    /**
     * Show/Hide toaster based on scroll direction
     */
    handleDisplay() {
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        const scrollHeight = scrollTop - this.state.lastScrollTop;
        const isScrollDown = scrollTop > this.state.lastScrollTop;

        if (isScrollDown) {
            if (scrollHeight >= this.options.autoDisplayTreshold) {
                this.toggleDisplay(true);
            }
        } else {
            this.state.lastScrollTop = scrollTop <= 0 ? 0 : scrollTop; // For Mobile or negative scrolling
        }
    }

    /**
     * Method to handle toaster.close event
     */
    onToasterClose() {
        this.remove();
    }

    /**
     * Method to be executed right before overlay close
     */
    onOverlayCloseBefore() {
        this.close();
    }

    /**
     * Method to handle modal.open event
     */
    onModalOpen() {
        this.close();
    }

    /**
     * Method to handle close btn clicks. Might be two cases:
     * 1) when toaster is collapsed, it removes toaster from page
     * 2) when toaster is expanded, it collapses toaster
     * @param {Object} e event object
     */
    onClose(e) {
        e.stopPropagation();
        if (this.options.declineWithCloseButton) {
            this.onDecline();
        } else if (!this.state.isOpen) {
            this.remove();
        } else {
            this.close();
        }
    }

    /**
     * Method to handle decline btn clicks. Sets cookie and removes toaster from page
     */
    onDecline() {
        this.closeOverlay();
        let duration = parseInt(this.options.cookieDuration, 10);
        duration = typeof duration === 'number' ? duration : 0;
        if (duration > 0) {
            cookie.setCookie('toasterDisabled', 1, duration);
        }
        this.remove();
    }

    /**
     * Method to handle callback calls
     */
    onTriggerToggleDisplay() {
        this.toggleDisplay();
    }

    /**
     * Accessibility handling
    */
    setAccessibility() {
        // inform screen reader users that a custom dialog is being inserted
        this.selectors.container.setAttribute('role', 'dialog');
        this.selectors.container.setAttribute('aria-modal', 'true');
        if (this.options.ariaLabel) {
            this.selectors.container.setAttribute('aria-label', this.options.ariaLabel);
        } else {
            this.selectors.container.setAttribute('aria-labelledby', 'toaster-title');
        }
    }

    /**
     * Remove accessibility handling
    */
    removeAccessibility() {
        this.selectors.container.removeAttribute('role');
        this.selectors.container.removeAttribute('aria-modal');
    }

    /**
     * Tab key event
     *
     * @param {Object} event Event object
    */
    onTabKey(event) {
        super.focusTrap(this.element, event);
    }

    /**
     * Keydown event handler
     *
     * @param {Object} event Event object
     */
    onKeyboardControl(event) {
        if (this.state.isOpen) {
            switch (event.key) {
            case 'Tab': // 'Tab' key
                this.onTabKey(event);
                break;
            case 'Escape': // 'Escape' key
                this.close();
                break;
            default:
                break;
            }
        }
    }

    /**
     * Check if Toaster is hidden through CSS
     *
     * @returns {Boolean} - true or false
     */
    isHidden() {
        const style = window.getComputedStyle(this.element);
        return (style.display === 'none') || DataLayer.get('page.isPageDesignerEditMode', false);
    }

    /**
     * Method to handle Toaster complete removal
     */
    remove() {
        this.element.remove();
    }

    /**
     * Method to close Toaster
     */
    close() {
        if (this.state.isOpen) {
            this.closeOverlay();
            this.state.isOpen = false;
            this.element.classList.remove('m-expanded');

            Event.emit('page.scroll.enabled', { isUnblockScrollForAll: this.options.showOverlay });
            this.removeAccessibility();
            this.destroyModalPattern();
            this.selectors.closeBtn.classList.remove('h-hidden');
            this.selectors.closeDialogBtn.classList.add('h-hidden');
            this.selectors.titleBtn.removeAttribute('tabindex');
            this.selectors.titleBtn.removeAttribute('aria-hidden');
            this.selectors.titleBtn.setAttribute('aria-expanded', false);
        }
    }

    /**
     * Method to close overlay
     */
    closeOverlay() {
        if (this.options.showOverlay) {
            Event.emit('overlay.close', {
                preventClick: false,
            });
        }
    }

    /**
     * Method to open overlay
     */
    openOverlay() {
        if (this.options.showOverlay) {
            Event.emit('overlay.open', {
                preventClick: false,
            });
        }
    }

    /**
     * Method to open Toaster
     */
    open() {
        Event.emit('page.scroll.disabled', this.options.showOverlay);
        Event.emit('analytics.event', this.analytics);

        this.openOverlay();
        this.state.isOpen = true;
        this.state.isFirstOpen = true;
        this.element.classList.add('m-expanded');
        this.setAccessibility();
        this.selectors.titleBtn.setAttribute('tabindex', '-1');
        this.selectors.titleBtn.setAttribute('aria-hidden', 'true');
        this.selectors.titleBtn.setAttribute('aria-expanded', true);
        this.selectors.closeBtn.classList.add('h-hidden');
        this.selectors.closeDialogBtn.classList.remove('h-hidden');

        this.initModalPattern(this.selectors.container);
    }

    /**
     * Toggle toaster display
     * @param {Boolean} forceOpen true to force toaster content to be displayed
     */
    toggleDisplay(forceOpen) {
        if (forceOpen) {
            if (!this.state.isOpen) {
                this.open();
            }
        } else if (this.state.isOpen) {
            this.close();
        } else {
            this.open();
        }
    }

    /**
     * Destroy is called automatically after the component is being removed from the DOM
     * You must always destroy the listeners attached to an element to avoid any memory leaks
     */
    destroy() {
        off('click', this.selectors.closeBtn);
        if (this.selectors.declineBtn) {
            off('click', this.selectors.declineBtn);
        }
        off('click', this.selectors.heading);
        off('click', this.selectors.closeDialogBtn);
        off('keydown', this.selectors.container);
        Event.removeListener('toaster.close', this.onToasterClose, this);
        if (this.options.showOverlay) {
            Event.removeListener('overlay.close.before', this.onOverlayCloseBefore, this);
        }
        Event.removeListener('modal.open', this.onModalOpen, this);
    }
}
